import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaUserTag, FaCheckCircle, FaTimesCircle, FaUser,
  FaEnvelope, FaPhone, FaShieldAlt, FaCalendarAlt, FaFileAlt,
  FaSpinner, FaExclamationCircle, FaExpand, FaTimes,
} from 'react-icons/fa';
import { getRoleRequestById, approveRoleRequest, rejectRoleRequest } from '../../../api/services/adminService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import './KYCManagement.css';
import './RoleRequests.css';


/* ─── helpers ─── */
const ROLE_MAP = { LANDLORD: 'Landlord', BNB_HOST: 'BnB Host', AGENT: 'Agent', GUEST: 'Guest', TENANT: 'Tenant' };
const formatRole = (r) => ROLE_MAP[r] || r || '—';
const formatDate = (ds) => ds
  ? new Date(ds).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';
const STATUS_MAP = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected', REJECTED_RESUBMIT_ALLOWED: 'rejected' };
const isPDF = (url) => { const l = (url || '').toLowerCase(); return l.endsWith('.pdf') || l.includes('.pdf?'); };

/* ─── Lightbox ─── */
const Lightbox = ({ blobUrl, label, isPdf, onClose }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="kycd-lightbox-overlay" onClick={onClose}>
      <div className="kycd-lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="kycd-lightbox-header">
          <span className="kycd-lightbox-label"><FaFileAlt /> {label}</span>
          <button type="button" className="kycd-lightbox-close" onClick={onClose} title="Close">
            <FaTimes />
          </button>
        </div>
        <div className="kycd-lightbox-body">
          {isPdf
            ? <iframe src={blobUrl} title={label} className="kycd-lightbox-iframe" />
            : <img src={blobUrl} alt={label} className="kycd-lightbox-img" />}
        </div>
      </div>
    </div>
  );
};

/* ─── DocCard ─── */
const DocCard = ({ label, originalUrl }) => {
  const [blobUrl, setBlobUrl]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!originalUrl || !originalUrl.startsWith('http')) return;
    let cancelled = false;
    setLoading(true); setError(false);
    fetchAuthenticatedImage(originalUrl)
      .then((url) => { if (!cancelled) { setBlobUrl(url); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [originalUrl]);

  if (!originalUrl) return null;

  return (
    <>
      <div className="kycd-doc-card">
        <div className="kycd-doc-label">
          <FaFileAlt /><span>{label}</span>
          {blobUrl && (
            <button type="button" className="kycd-doc-open" onClick={() => setLightbox(true)} title="View full size">
              <FaExpand />
            </button>
          )}
        </div>
        <div className="kycd-doc-preview" onClick={() => blobUrl && setLightbox(true)}
          style={{ cursor: blobUrl ? 'zoom-in' : 'default' }}>
          {loading && <div className="kycd-doc-loading"><FaSpinner className="kycd-spin" /><span>Loading…</span></div>}
          {error  && <div className="kycd-doc-error"><FaExclamationCircle /><span>Failed to load</span></div>}
          {!loading && !error && blobUrl && (
            isPDF(originalUrl)
              ? <iframe src={blobUrl} title={label} className="kycd-doc-iframe" />
              : <img src={blobUrl} alt={label} className="kycd-doc-img" />
          )}
          {!loading && !error && !blobUrl && originalUrl && (
            <div className="kycd-doc-loading"><FaFileAlt style={{ fontSize: 28, opacity: 0.3 }} /></div>
          )}
        </div>
        {blobUrl && (
          <div className="kycd-doc-footer">
            <button type="button" className="kycd-doc-expand-btn" onClick={() => setLightbox(true)}>
              <FaExpand /> View Full Size
            </button>
          </div>
        )}
      </div>
      {lightbox && blobUrl && (
        <Lightbox blobUrl={blobUrl} label={label} isPdf={isPDF(originalUrl)} onClose={() => setLightbox(false)} />
      )}
    </>
  );
};

/* ─── InfoRow ─── */
const InfoRow = ({ icon, label, value }) => (
  <div className="kycd-info-row">
    <div className="kycd-info-icon">{icon}</div>
    <div>
      <div className="kycd-info-label">{label}</div>
      <div className="kycd-info-value">{value || '—'}</div>
    </div>
  </div>
);

/* ─── Status badge ─── */
const StatusBadge = ({ status }) => {
  const cfg = {
    pending:  { label: 'Pending Review', cls: 'rr-badge--pending'  },
    approved: { label: 'Approved',       cls: 'rr-badge--approved' },
    rejected: { label: 'Rejected',       cls: 'rr-badge--rejected' },
  }[status] || { label: status, cls: '' };
  return <span className={`rr-badge rr-badge--lg ${cfg.cls}`}>{cfg.label}</span>;
};

/* ─── KYC Pill ─── */
const KycPill = ({ status, label }) => {
  const map = {
    APPROVED: { cls: 'rr-kyc-pill--ok',      text: `${label}: Verified ✓` },
    VERIFIED: { cls: 'rr-kyc-pill--ok',      text: `${label}: Verified ✓` },
    PENDING:  { cls: 'rr-kyc-pill--pending', text: `${label}: Pending` },
    REJECTED: { cls: 'rr-kyc-pill--fail',    text: `${label}: Rejected ✗` },
    NONE:     { cls: 'rr-kyc-pill--none',    text: `${label}: Not submitted` },
  };
  const key = (status || 'NONE').toUpperCase();
  const { cls, text } = map[key] || { cls: 'rr-kyc-pill--none', text: `${label}: ${status || '—'}` };
  return <span className={`rr-kyc-pill ${cls}`}>{text}</span>;
};

/* ─── Reject Modal ─── */
const REASON_CODES = [
  { value: 'MISSING_KYC',           label: 'KYC not submitted or incomplete' },
  { value: 'MISSING_KYB',           label: 'KYB not submitted or incomplete' },
  { value: 'INSUFFICIENT_INFO',     label: 'Insufficient information provided' },
  { value: 'DUPLICATE_REQUEST',     label: 'Duplicate / existing role request' },
  { value: 'NOT_ELIGIBLE',          label: 'Does not meet role eligibility criteria' },
  { value: 'SUSPICIOUS_ACTIVITY',   label: 'Suspicious or fraudulent activity suspected' },
  { value: 'OTHER',                 label: 'Other (specify below)' },
];

const RejectModal = ({ onClose, onSubmit, busy }) => {
  const [reason, setReason]     = useState('');
  const [code, setCode]         = useState('');
  const [allowResub, setResub]  = useState(false);
  const [deadline, setDeadline] = useState('');
  const [err, setErr]           = useState('');

  const submit = () => {
    if (!code)         { setErr('Please select a reason code.'); return; }
    if (!reason.trim()){ setErr('Please enter a rejection reason.'); return; }
    onSubmit(code, reason.trim(), allowResub, deadline || null);
  };

  return (
    <div className="kycd-overlay" onClick={onClose}>
      <div className="kycd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kycd-modal-header">
          <h2><FaTimesCircle className="kycd-modal-icon kycd-modal-icon--reject" /> Reject Role Request</h2>
          <button type="button" className="kycd-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="kycd-modal-body">
          <label className="kycd-field-label">Reason Code <span className="kycd-req">*</span></label>
          <select className="kycd-select" value={code} onChange={(e) => setCode(e.target.value)} disabled={busy}>
            <option value="">Select a code…</option>
            {REASON_CODES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>

          <label className="kycd-field-label" style={{ marginTop: 16 }}>
            Rejection Reason <span className="kycd-req">*</span>
          </label>
          <textarea className="kycd-textarea" rows={4} disabled={busy}
            placeholder="Explain why the request is being rejected. This will be visible to the user."
            value={reason} onChange={(e) => setReason(e.target.value)} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
            <input type="checkbox" id="rr-allow-resub" checked={allowResub}
              onChange={(e) => setResub(e.target.checked)} disabled={busy} />
            <label htmlFor="rr-allow-resub" style={{ fontSize: 13.5, color: 'var(--admin-text)', cursor: 'pointer' }}>
              Allow resubmission
            </label>
          </div>

          {allowResub && (
            <div style={{ marginTop: 12 }}>
              <label className="kycd-field-label">Resubmit Deadline (optional)</label>
              <input type="date" className="kycd-select" value={deadline}
                onChange={(e) => setDeadline(e.target.value)} disabled={busy} />
            </div>
          )}

          {err && <div className="kycd-field-err">{err}</div>}
        </div>
        <div className="kycd-modal-footer">
          <button type="button" className="kycd-btn kycd-btn--outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="kycd-btn kycd-btn--reject" onClick={submit} disabled={busy}>
            {busy ? <><FaSpinner className="kycd-spin" /> Rejecting…</> : 'Confirm Rejection'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Toast ─── */
const Toast = ({ type, message, onClose }) => (
  <div className={`kycd-toast kycd-toast--${type}`}>
    {type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
    <span>{message}</span>
    <button type="button" onClick={onClose}>×</button>
  </div>
);

/* ─── Collect all document URLs from raw data ─── */
const collectDocs = (raw) => {
  if (!raw) return [];
  const DOC_FIELDS = [
    'documentUrl', 'document', 'fileUrl', 'file', 'imageUrl', 'image',
    'attachmentUrl', 'attachment', 'idDocumentUrl', 'idDocumentBackUrl',
    'selfieUrl', 'businessDocumentUrl', 'businessDocumentUrl2', 'businessDocumentUrl3',
  ];
  const docs = [];
  DOC_FIELDS.forEach((f) => {
    if (raw[f] && typeof raw[f] === 'string' && raw[f].startsWith('http')) {
      const pretty = f.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).replace(/Url$/, '').trim();
      docs.push({ key: f, url: raw[f], label: pretty });
    }
    // Numbered variants
    for (let i = 2; i <= 5; i++) {
      const fk = `${f}${i}`;
      if (raw[fk] && typeof raw[fk] === 'string' && raw[fk].startsWith('http')) {
        docs.push({ key: fk, url: raw[fk], label: `${f.replace(/Url$/, '')} ${i}` });
      }
    }
  });
  return docs;
};

/* ─── Main ─── */
const RoleRequestDetails = () => {
  const { requestId } = useParams();
  const navigate      = useNavigate();
  const [req, setReq]           = useState(null);
  const [raw, setRaw]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [busy, setBusy]         = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [toast, setToast]       = useState(null);

  const showToast = (type, msg) => { setToast({ type, message: msg }); setTimeout(() => setToast(null), 4500); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await getRoleRequestById(requestId);
      const data = res?.data || res;
      setRaw(data);
      const u = data.user || {};
      const roles = Array.isArray(u.roles) ? u.roles : [];
      const primaryRole = roles.find((r) => r.isPrimary)?.role || roles[0]?.role;
      setReq({
        id:              data.id,
        name:            `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—',
        email:           u.email || '—',
        phone:           u.phone || '—',
        currentRole:     formatRole(primaryRole),
        requestedRole:   formatRole(data.role),
        status:          STATUS_MAP[(data.status || '').toUpperCase()] || (data.status || '').toLowerCase(),
        reason:          data.reason || null,
        rejectionReason: data.rejectionReason || null,
        rejectionCode:   data.rejectionReasonCode || null,
        submittedAt:     formatDate(data.requestedAt),
        reviewedAt:      data.reviewedAt ? formatDate(data.reviewedAt) : null,
        resubmitDeadline:data.resubmitDeadline ? formatDate(data.resubmitDeadline) : null,
        kycStatus:       u.kycStatus || null,
        kybStatus:       u.kybStatus || null,
        idNumber:        u.idNumber || null,
      });
    } catch (e) { setError(e.message || 'Failed to load request'); }
    finally    { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!window.confirm('Approve this role request?')) return;
    setBusy(true);
    try { await approveRoleRequest(requestId); showToast('success', 'Role request approved.'); await load(); }
    catch (e) { showToast('error', e.message || 'Failed to approve.'); }
    finally   { setBusy(false); }
  };

  const handleRejectSubmit = async (code, reason, allowResub, deadline) => {
    setBusy(true);
    try {
      await rejectRoleRequest(requestId, code, reason, allowResub, deadline);
      setRejectOpen(false);
      showToast('success', 'Role request rejected.');
      await load();
    } catch (e) { showToast('error', e.message || 'Failed to reject.'); }
    finally    { setBusy(false); }
  };

  if (loading) return (
    <div className="kycd-page">
      <button type="button" className="kycd-back" onClick={() => navigate('/system-admin/role-requests')}>
        <FaArrowLeft /> Back to Role Requests
      </button>
      <div className="kycd-loading"><FaSpinner className="kycd-spin kycd-spin--lg" /><p>Loading request…</p></div>
    </div>
  );

  if (error || !req) return (
    <div className="kycd-page">
      <button type="button" className="kycd-back" onClick={() => navigate('/system-admin/role-requests')}>
        <FaArrowLeft /> Back to Role Requests
      </button>
      <div className="kyc-alert kyc-alert--error" style={{ marginTop: 24 }}>
        {error || 'Request not found'}
        <button type="button" className="kyc-alert-retry" onClick={load}>Retry</button>
      </div>
    </div>
  );

  const isPending  = req.status === 'pending';
  const isRejected = req.status === 'rejected';
  const docs = collectDocs(raw);

  return (
    <div className="kycd-page">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <button type="button" className="kycd-back" onClick={() => navigate('/system-admin/role-requests')}>
        <FaArrowLeft /> Back to Role Requests
      </button>

      {/* Hero */}
      <div className="kycd-hero">
        <div className="kycd-hero-avatar">{(req.name || '?')[0].toUpperCase()}</div>
        <div className="kycd-hero-info">
          <div className="kycd-hero-name">{req.name}</div>
          <div className="kycd-hero-email">{req.email}</div>
          <div className="kycd-hero-meta">
            <StatusBadge status={req.status} />
            <span className="kycd-hero-chip">{req.currentRole} → {req.requestedRole}</span>
            <KycPill status={req.kycStatus} label="KYC" />
            {req.kybStatus && <KycPill status={req.kybStatus} label="KYB" />}
          </div>
        </div>
        {isPending && (
          <div className="kycd-hero-actions">
            <button type="button" className="kycd-btn kycd-btn--approve" onClick={handleApprove} disabled={busy}>
              {busy ? <FaSpinner className="kycd-spin" /> : <FaCheckCircle />} Approve
            </button>
            <button type="button" className="kycd-btn kycd-btn--reject" onClick={() => setRejectOpen(true)} disabled={busy}>
              <FaTimesCircle /> Reject
            </button>
          </div>
        )}
      </div>

      {/* Banners */}
      {req.status === 'approved' && req.reviewedAt && (
        <div className="kycd-banner kycd-banner--success"><FaCheckCircle /> Approved on {req.reviewedAt}</div>
      )}
      {isRejected && (
        <div className="kycd-banner kycd-banner--danger">
          <FaTimesCircle />
          <div>
            <strong>Rejected</strong>
            {req.rejectionCode && <span className="kycd-rejection-code"> [{req.rejectionCode}]</span>}
            {req.rejectionReason && <div className="kycd-rejection-text">{req.rejectionReason}</div>}
            {req.resubmitDeadline && <div className="kycd-rejection-deadline">Resubmit by: {req.resubmitDeadline}</div>}
            {req.reviewedAt && <div className="kycd-rejection-deadline">Reviewed on {req.reviewedAt}</div>}
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div className="kycd-columns">
        {/* Info */}
        <div className="kycd-col-info">
          <div className="kycd-card">
            <div className="kycd-card-header"><FaUser /> Applicant Information</div>
            <div className="kycd-card-body">
              <InfoRow icon={<FaUser />}       label="Full Name"     value={req.name} />
              <InfoRow icon={<FaEnvelope />}   label="Email"         value={req.email} />
              <InfoRow icon={<FaPhone />}      label="Phone"         value={req.phone} />
              {req.idNumber && <InfoRow icon={<FaShieldAlt />} label="National ID" value={req.idNumber} />}
            </div>
          </div>

          <div className="kycd-card" style={{ marginTop: 16 }}>
            <div className="kycd-card-header"><FaUserTag /> Role Request Details</div>
            <div className="kycd-card-body">
              <InfoRow icon={<FaUserTag />}    label="Current Role"    value={req.currentRole} />
              <InfoRow icon={<FaUserTag />}    label="Requested Role"  value={req.requestedRole} />
              <InfoRow icon={<FaCalendarAlt />} label="Submitted At"  value={req.submittedAt} />
              {req.reviewedAt && <InfoRow icon={<FaCalendarAlt />} label="Reviewed At" value={req.reviewedAt} />}
              {req.reason && <InfoRow icon={<FaFileAlt />} label="Applicant Reason" value={req.reason} />}
            </div>
          </div>

          <div className="kycd-card" style={{ marginTop: 16 }}>
            <div className="kycd-card-header"><FaShieldAlt /> Verification Status</div>
            <div className="kycd-card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div className="kycd-info-label" style={{ marginBottom: 6 }}>KYC (Identity)</div>
                  <KycPill status={req.kycStatus} label="KYC" />
                </div>
                {req.kybStatus && (
                  <div>
                    <div className="kycd-info-label" style={{ marginBottom: 6 }}>KYB (Business)</div>
                    <KycPill status={req.kybStatus} label="KYB" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions for rejected */}
          {isRejected && (
            <div className="kycd-card" style={{ marginTop: 16 }}>
              <div className="kycd-card-header"><FaShieldAlt /> Actions</div>
              <div className="kycd-card-body">
                <p className="kycd-action-hint">You can override and approve this request, or re-reject with updated reasons.</p>
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button type="button" className="kycd-btn kycd-btn--approve" onClick={handleApprove} disabled={busy} style={{ flex: 1 }}>
                    {busy ? <FaSpinner className="kycd-spin" /> : <FaCheckCircle />} Override &amp; Approve
                  </button>
                  <button type="button" className="kycd-btn kycd-btn--reject" onClick={() => setRejectOpen(true)} disabled={busy} style={{ flex: 1 }}>
                    <FaTimesCircle /> Re-Reject
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="kycd-col-docs">
          <div className="kycd-card">
            <div className="kycd-card-header"><FaFileAlt /> Supporting Documents</div>
            <div className="kycd-card-body kycd-card-body--docs">
              {docs.length === 0 ? (
                <div className="kycd-no-docs">
                  <FaFileAlt />
                  <p>No supporting documents were submitted with this request.</p>
                </div>
              ) : (
                <div className="kycd-docs-grid">
                  {docs.map((d) => <DocCard key={d.key} label={d.label} originalUrl={d.url} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {rejectOpen && (
        <RejectModal onClose={() => setRejectOpen(false)} onSubmit={handleRejectSubmit} busy={busy} />
      )}
    </div>
  );
};

export default RoleRequestDetails;
